import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useConvexAuth } from 'convex/react';
import posthog from 'posthog-js';
import { AlertCircle, RotateCcw } from 'lucide-react';

import { ToolsLayout } from '../../components/tools/ToolsLayout';
import { UploadSection } from '../../components/tools/bg-removal/UploadSection';
import { AuthModal } from '../../components/tools/bg-removal/AuthModal';
import { ProcessingView } from '../../components/tools/bg-removal/ProcessingView';
import { ResultView } from '../../components/tools/bg-removal/ResultView';
import { FAQSection } from '../../components/tools/bg-removal/FAQSection';
import { useToolBgRemoval } from '../../hooks/use-tool-bg-removal';

type PageState = 'idle' | 'file_selected' | 'auth_prompt' | 'processing' | 'completed' | 'error';

const STRUCTURED_DATA = {
  softwareApplication: {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'ReelCat Remove Video Background',
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    url: 'https://reelcat.app/tools/remove-video-background',
    description:
      'Free AI-powered tool to remove video backgrounds automatically. No green screen needed. Supports MP4, WebM, and MOV files up to 1 minute.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    creator: {
      '@type': 'Organization',
      name: 'ReelCat',
      url: 'https://reelcat.app',
    },
  },

  faqPage: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I remove a video background without a green screen?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Simply upload your video to ReelCat and our AI will automatically detect and remove the background — no green screen, chroma key, or manual masking required. The AI model processes each frame to separate the subject from the background.',
        },
      },
      {
        '@type': 'Question',
        name: 'What video formats and sizes are supported?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We support MP4, WebM, and MOV video files. Videos can be up to 1 minute long and 100MB in size. Most smartphone and camera recordings work out of the box.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is it free to remove video backgrounds?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, the background removal tool is free to use. You just need to create a free ReelCat account to process your video. There are no watermarks on the output.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does processing take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most videos are processed in 30 to 90 seconds depending on the length and resolution. The AI runs on dedicated GPU servers for fast turnaround.',
        },
      },
      {
        '@type': 'Question',
        name: 'What can I do with the background-removed video?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can download the transparent video file directly, or click "Edit in ReelCat" to open it in our full video editor where you can add new backgrounds, captions, effects, music, and more.',
        },
      },
    ],
  },

  howTo: {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Remove a Video Background with AI',
    description:
      'Remove video backgrounds automatically using ReelCat\'s free AI tool. No green screen or editing skills needed.',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Upload your video',
        text: 'Drag and drop or select a video file (MP4, WebM, or MOV). Maximum 1 minute, 100MB.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Sign in to your free account',
        text: 'Create a free ReelCat account with your email or Google to start processing.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Download or edit your result',
        text: 'Once processing is complete, download the transparent video or open it in the ReelCat editor to add new backgrounds, captions, and effects.',
      },
    ],
    totalTime: 'PT2M',
    tool: {
      '@type': 'HowToTool',
      name: 'ReelCat Remove Video Background tool',
    },
  },

  breadcrumb: {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://reelcat.app',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Tools',
        item: 'https://reelcat.app/tools',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Remove Video Background',
        item: 'https://reelcat.app/tools/remove-video-background',
      },
    ],
  },
};

export function RemoveBackground() {
  const { isAuthenticated } = useConvexAuth();
  const [pageState, setPageState] = useState<PageState>('idle');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const {
    status: processingStatus,
    projectId,
    processedVideoUrl,
    originalVideoUrl,
    processedStorageId,
    videoStorageId,
    error,
    processFile,
    reset: resetProcessing,
  } = useToolBgRemoval();

  // Track page view
  useEffect(() => {
    posthog.capture('tool_page_view', { tool: 'remove-video-background' });
  }, []);

  // Sync processing hook status with page state
  useEffect(() => {
    if (processingStatus === 'uploading' || processingStatus === 'processing') {
      setPageState('processing');
    } else if (processingStatus === 'completed') {
      setPageState('completed');
    } else if (processingStatus === 'error') {
      setPageState('error');
    }
  }, [processingStatus]);

  const startProcessing = useCallback((file: File) => {
    setPageState('processing');
    processFile(file);
  }, [processFile]);

  const handleFileSelected = useCallback((file: File) => {
    posthog.capture('tool_file_selected');
    setPendingFile(file);

    if (isAuthenticated) {
      startProcessing(file);
    } else {
      setPageState('auth_prompt');
      setAuthModalOpen(true);
      posthog.capture('tool_auth_started');
    }
  }, [isAuthenticated, startProcessing]);

  const handleAuthenticated = useCallback(() => {
    posthog.capture('tool_auth_completed');
    if (pendingFile) {
      startProcessing(pendingFile);
    }
  }, [pendingFile, startProcessing]);

  const handleReset = useCallback(() => {
    setPendingFile(null);
    setPageState('idle');
    resetProcessing();
  }, [resetProcessing]);

  const handleRetry = useCallback(() => {
    if (pendingFile) {
      resetProcessing();
      startProcessing(pendingFile);
    }
  }, [pendingFile, resetProcessing, startProcessing]);

  return (
    <ToolsLayout>
      <Helmet>
        <title>Remove Video Background Free | ReelCat</title>
        <meta name="description" content="Remove video backgrounds automatically with AI. No green screen needed. Free online tool — supports MP4, WebM, MOV. Download transparent video or edit in ReelCat." />
        <meta name="application-name" content="ReelCat" />
        <meta property="og:title" content="Remove Video Background Free | ReelCat" />
        <meta property="og:description" content="Remove video backgrounds automatically with AI. No green screen needed." />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ReelCat" />
        <meta property="og:url" content="https://reelcat.app/tools/remove-video-background" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Remove Video Background Free | ReelCat" />
        <meta name="twitter:description" content="Remove video backgrounds automatically with AI. No green screen needed." />
        <link rel="canonical" href="https://reelcat.app/tools/remove-video-background" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(STRUCTURED_DATA.softwareApplication)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(STRUCTURED_DATA.faqPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(STRUCTURED_DATA.howTo)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(STRUCTURED_DATA.breadcrumb)}
        </script>
      </Helmet>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        onAuthenticated={handleAuthenticated}
      />

      {/* Page content based on state */}
      {(pageState === 'idle' || pageState === 'file_selected' || pageState === 'auth_prompt') && (
        <>
          <UploadSection onFileSelected={handleFileSelected} />
          <FAQSection />
        </>
      )}

      {pageState === 'processing' && pendingFile && (
        <ProcessingView file={pendingFile} />
      )}

      {pageState === 'completed' && processedVideoUrl && originalVideoUrl && projectId && processedStorageId && videoStorageId && (
        <ResultView
          originalVideoUrl={originalVideoUrl}
          processedVideoUrl={processedVideoUrl}
          processedStorageId={processedStorageId}
          projectId={projectId}
          videoStorageId={videoStorageId}
          onReset={handleReset}
        />
      )}

      {pageState === 'error' && (
        <div className="flex flex-col items-center gap-6 py-12">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              {error || 'An unexpected error occurred while processing your video.'}
            </p>
          </div>
          <div className="flex gap-3">
            {pendingFile && (
              <button
                onClick={handleRetry}
                className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Try Again
              </button>
            )}
            <button
              onClick={handleReset}
              className="px-5 py-2.5 border border-border rounded-lg font-medium hover:bg-muted transition-colors"
            >
              Upload Different Video
            </button>
          </div>
        </div>
      )}
    </ToolsLayout>
  );
}
